import { useSyncExternalStore } from "react";
import type { CatalogueItem } from "@shared-core/data/catalogue-types";

// An in-memory working-copy store for the catalogue editor, keyed by catalogue
// id. Item CRUD accumulates into one working copy; Save commits it via the
// management API. newItemIds and modifiedItemIds tell the save handler which
// API calls to make.

export interface CatalogueDraft {
  items: CatalogueItem[];
  dirty: boolean;
  /** Ids of items created in this editing session (not yet persisted). */
  newItemIds: ReadonlySet<string>;
  /** Ids of existing items that have been edited in this session. */
  modifiedItemIds: ReadonlySet<string>;
  /**
   * Ids whose real fields/quantity breaks are known — either hydrated from
   * the resolved-item endpoint, or authored fresh this session (new/modified
   * items are hydrated by construction; there is nothing on the server yet
   * to fetch).
   *
   * Exists so a caller can ask "is it safe to render the editable panel for
   * this id?" as one fact rather than re-deriving it. The item-URL route uses
   * it as a render precondition: the panel mounts only once the id is in this
   * set, which is what makes "do not open on a failed hydration" hold even
   * when the id arrives from a shared link instead of a grid click.
   *
   * Deliberately not inferred from shape (e.g. "fields.length === 0"): an
   * item that genuinely has zero fields and zero breaks would never satisfy
   * that inference, and a render gate built on it would hang forever.
   */
  hydratedItemIds: ReadonlySet<string>;
}

const EMPTY: CatalogueDraft = {
  items: [],
  dirty: false,
  newItemIds: new Set(),
  modifiedItemIds: new Set(),
  hydratedItemIds: new Set(),
};
const drafts = new Map<string, CatalogueDraft>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

/** Seed the working copy from the live items once; no-op if already started. */
export function ensureCatalogueDraft(id: string, seed: CatalogueItem[]): void {
  if (!drafts.has(id))
    drafts.set(id, {
      items: seed,
      dirty: false,
      newItemIds: new Set(),
      modifiedItemIds: new Set(),
      hydratedItemIds: new Set(),
    });
}

export function removeCatalogueItem(id: string, itemId: string): void {
  const d = drafts.get(id);
  if (!d) return;
  const newItemIds = new Set(d.newItemIds);
  const modifiedItemIds = new Set(d.modifiedItemIds);
  const hydratedItemIds = new Set(d.hydratedItemIds);
  newItemIds.delete(itemId);
  modifiedItemIds.delete(itemId);
  hydratedItemIds.delete(itemId);
  drafts.set(id, {
    items: d.items.filter((i) => i.id !== itemId),
    dirty: true,
    newItemIds,
    modifiedItemIds,
    hydratedItemIds,
  });
  emit();
}

export function upsertCatalogueItem(id: string, item: CatalogueItem): void {
  const d = drafts.get(id) ?? EMPTY;
  const exists = d.items.some((i) => i.id === item.id);
  const items = exists
    ? d.items.map((i) => (i.id === item.id ? item : i))
    : [...d.items, item];
  const newItemIds = new Set(d.newItemIds);
  const modifiedItemIds = new Set(d.modifiedItemIds);
  if (item.id.startsWith("new-")) {
    newItemIds.add(item.id);
  } else {
    modifiedItemIds.add(item.id);
  }
  // An item the user just authored or edited is known by construction —
  // there is nothing on the server yet that a fetch could add.
  const hydratedItemIds = new Set(d.hydratedItemIds);
  hydratedItemIds.add(item.id);
  drafts.set(id, {
    items,
    dirty: true,
    newItemIds,
    modifiedItemIds,
    hydratedItemIds,
  });
  emit();
}

/**
 * Replace an item in the draft with its fully-resolved version (real fields +
 * quantity breaks) WITHOUT marking it modified or the draft dirty.
 *
 * The grid loads items as tiles, which carry no fields or quantity breaks. If
 * the user opens such a tile and saves, the empty config would overwrite the
 * item's real fields/breaks on the server. Hydrating the resolved item on open
 * means a subsequent save round-trips the real config. Skips items the user has
 * already created or edited this session, so it never clobbers in-progress work.
 *
 * Inserts the item if it is missing from the draft's snapshot rather than
 * no-opping, provided it belongs to this catalogue: a URL-first entry (a
 * shared link, a refresh) has no grid render to have populated the tile list
 * first, so "missing tile" is a real, resolvable case here, not a bug. An
 * item belonging to a *different* catalogue still no-ops — that mismatch is
 * surfaced by the caller as its own error, not silently absorbed here.
 */
export function hydrateCatalogueItem(id: string, item: CatalogueItem): void {
  const d = drafts.get(id);
  if (!d) return;
  if (item.catalogueId !== id) return;
  if (d.newItemIds.has(item.id) || d.modifiedItemIds.has(item.id)) return;
  const exists = d.items.some((i) => i.id === item.id);
  const hydratedItemIds = new Set(d.hydratedItemIds);
  hydratedItemIds.add(item.id);
  drafts.set(id, {
    ...d,
    items: exists
      ? d.items.map((i) => (i.id === item.id ? item : i))
      : [...d.items, item],
    hydratedItemIds,
  });
  emit();
}

export function revertCatalogueDraft(id: string, seed: CatalogueItem[]): void {
  drafts.set(id, {
    items: seed,
    dirty: false,
    newItemIds: new Set(),
    modifiedItemIds: new Set(),
    // Reseeding from tiles means every item is unhydrated again. A component
    // reading this store re-derives readiness live rather than caching it, so
    // clearing this here is what makes a post-revert re-open refetch rather
    // than showing stale (or, worse, no longer relevant) content.
    hydratedItemIds: new Set(),
  });
  emit();
}

/** Read a catalogue's working copy without subscribing (for save handlers/tests). */
export function readCatalogueDraft(id: string): CatalogueDraft {
  return drafts.get(id) ?? EMPTY;
}

/** Subscribe a component to a catalogue's working copy. */
export function useCatalogueDraft(id: string): CatalogueDraft {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => drafts.get(id) ?? EMPTY,
  );
}
